import { LoadingButton } from '@mui/lab';
import { Box, TextField } from '@mui/material';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FORM_STATUS } from '../../model/formStatus';
import accountService from '../../services/AccountService';

const defaultState = {
  email: '',
};

const ForgotPasswordComponent: React.FC = () => {
  const { t } = useTranslation('auth');
  const [formData, setFormData] = useState(defaultState);
  const [status, setStatus] = useState(FORM_STATUS.IDLE);
  const [touched, setTouched] = useState(defaultState);

  const [statusMessage, setStatusMessage] = useState(
    'Please wait while we verify your email address'
  );
  const [requestSuccess, setRequestsuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  function handleChange(e: any) {
    e.persist();
    setFormData((curFormData) => {
      return {
        ...curFormData,
        [e.target.id]: e.target.value,
      };
    });
  }

  function handleBlur(e: any) {
    e.persist();
    setTouched((cur: any) => {
      return { ...cur, [e.target.id]: true };
    });
  }

  function getErrors(formData: any) {
    const result = {
      email: '',
    };
    if (status === FORM_STATUS.COMPLETED) return result;

    if (!formData.email) {
      result.email = t('validation.emailRequired');
    } else if (!/^.+@.+$/i.test(formData.email)) {
      result.email = t('validation.emailInvalid');
    }

    return result;
  }

  //Derived state
  const errors = getErrors(formData);
  const isValid = Object.values(errors).every((x) => x === null || x === '');

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus(FORM_STATUS.SUBMITTING);
    if (isValid) {
      setIsLoading(true);
      accountService
        .forgotPassword(formData.email)
        .then((response: any) => {
          setStatus(FORM_STATUS.COMPLETED);
          setFormData(defaultState);
          setRequestsuccess(true);
          setStatusMessage(response?.message ?? '');
        })
        .catch((error: any) => {
          setStatusMessage(error?.message ?? t('genericError'));
          setRequestsuccess(false);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setStatus(FORM_STATUS.SUBMITTED);
    }
  };

  return (
    <>
      <Typography variant="h4">
        <strong>{t('forgotPassword.title')}</strong>
      </Typography>

      <Box component="form" noValidate sx={{ mt: 2 }} onSubmit={onSubmit}>
        <TextField
          required
          fullWidth
          id="email"
          label={t('forgotPassword.emailLabel')}
          name="email"
          autoComplete="email"
          value={formData.email}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={status === FORM_STATUS.COMPLETED ? true : false}
          helperText={
            (touched.email || status === FORM_STATUS.SUBMITTED) && errors.email
          }
          error={
            (touched.email !== '' || status === FORM_STATUS.SUBMITTED) &&
            errors.email !== ''
          }
        />
        <LoadingButton
          type="submit"
          loading={isLoading}
          variant="contained"
          color="primary"
          sx={{ mt: 3 }}
          disabled={status === FORM_STATUS.COMPLETED ? true : false}
        >
          {t('forgotPassword.submit')}
        </LoadingButton>
      </Box>
      <Box
        sx={{
          mt: 2,
          mb: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {requestSuccess ? (
          <>
            <Typography variant="body1" sx={{ mt: 1 }}>
              {statusMessage}
            </Typography>
            <Link href="/" variant="body2" sx={{ mt: 1 }}>
              {t('returnHome')}
            </Link>
          </>
        ) : (
          <Link href="/" variant="body2" sx={{ mt: 1 }}>
            click here to return home
          </Link>
        )}
      </Box>
    </>
  );
};

export default ForgotPasswordComponent;
